import Route from '@ember/routing/route';
import { service } from '@ember/service';

import { shouldSkipKyc } from '@repo/due-diligence-kit/kyc';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import { webviewManager } from '@repo/shared-kit/webview';
import { variation } from 'ember-launch-darkly';
import window from 'ember-window-mock';

import { jsURL } from 'register-partners-js/constants/hosts';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { postRegistrationSubmitAsync } from 'register-partners-js/react/registration';
import { ErrorInfo } from 'register-partners-js/utils/error-info';
import { fetchKyc } from 'register-partners-js/utils/fetch-kyc';
import { fetchNavigationOrganizationsClient } from 'register-partners-js/utils/fetch-navigation-organizations';

export default class RegistrationKycRoute extends Route {
  mainLayout = MainLayout;

  @service sentry;
  @service deviceManager;
  @service router;
  @service segment;
  @service store;
  @service networkManager;
  @service queryClientManager;
  @service launchdarkly;

  beforeModel() {
    let enableReactRoute = variation('feature--boolean-use-new-kyc-modules');

    if (enableReactRoute) {
      let registration = this.modelFor('registration');
      this.router.replaceWith('react', `registrations/${registration.id}/new-kyc`);
    }
  }

  async model() {
    let registration = this.modelFor('registration');
    let organization = registration.get('organization');
    let owner = await registration.get('owner');

    let isSingleKycEnabled = variation('feature--boolean-single-kyc');
    let kycInfo = await fetchKyc({
      sentry: this.sentry,
      queryClientManager: this.queryClientManager,
      isSingleKycEnabled,
      market: organization.get('legalCountry'),
    });

    if (shouldSkipKyc(kycInfo)) {
      this.segment.track('onboarding_partner_kyc_skipped', {
        membership_id: owner.id,
      });

      return {
        registration,
        organization,
        stakeholder: owner,
        contractSign: true,
      };
    } else if (owner.kycProviderName === 'onfido') {
      this.segment.track('onboarding_partner_kyc_started', {
        provider: owner.kycProviderName,
        is_webview: webviewManager.isWebview,
      });

      let kycSdkToken = this.store.createRecord('kyc-sdk-token', {
        stakeholder: owner,
      });

      await kycSdkToken.save();

      return {
        registration,
        organization,
        stakeholder: owner,
        kycSdkToken,
      };
    } else {
      this.segment.track('onboarding_partner_kyc_started', {
        provider: owner.kycProviderName,
        is_webview: webviewManager.isWebview,
      });
      this.segment.track('onboarding_partner_fourthline_waiting_screen_loaded');
      try {
        let kyc = this.store.createRecord('kyc', {
          stakeholder: owner,
        });
        await kyc.save();

        if (kyc.providerStatus === 'process_finished') {
          return {
            registration,
            organization,
            stakeholder: owner,
            contractSign: true,
          };
        } else {
          return {
            registration,
            organization,
            stakeholder: owner,
            kyc,
          };
        }
      } catch (error) {
        if (ErrorInfo.for(error).shouldSendToSentry) {
          this.sentry.captureException(error);
        }
        // We leverage the underlying error screen for fourthline that expects a reason.
        return {
          registration,
          stakeholder: owner,
          kyc: {
            reason: 'KYC_API_ERROR',
          },
        };
      }
    }
  }

  async afterModel({ registration, contractSign }) {
    if (contractSign) {
      if (variation('scaling--async-contract-signature')) {
        try {
          await postRegistrationSubmitAsync(registration.id);
        } catch (error) {
          if (shouldSendToSentry(error)) {
            this.sentry.captureException(error);
          }
        }
      } else {
        await registration.submit();

        let organization = registration.get('organization');
        let isGermanFlow = organization.legalCountry === 'DE';
        let isMobile = this.deviceManager.isMobile;

        if (isGermanFlow || isMobile) {
          return this.router.transitionTo('registrations.already-created', organization.id);
        } else {
          let organizations = await fetchNavigationOrganizationsClient();
          let org = organizations.find(o => o.id === organization.id);
          window.location.replace(`${jsURL}/organizations/${org.slug}`);
        }
      }
    }
  }

  setupController(controller, { contractSign }) {
    super.setupController(...arguments);

    if (contractSign) {
      controller.contractSignWebSocketEnabled = true;
    }
  }
}
